import styles from './MarketInsightVersionNotification.module.scss';
import { useAtom } from 'jotai';
import { marketInsightVersionStatusAtom } from 'store/marketInsightVersionStatusAtom';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import Chip from '@lotlinx-aws/sparky-component-library/Sparky/Chip';
import { useLocation } from 'react-router';
import {
  IconArrowRight,
  IconArrowUpRight,
  IconBulb,
  IconCircleFilled,
  IconSwitchHorizontal,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import Popover from '@lotlinx-aws/sparky-component-library/Sparky/Popover';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import { useNavigateKeepParams } from 'components/KeepParamsRouter/KeepParamsRouter';
import PulsingCircleAnimation from './PulsingCircleAnimation/PulsingCircleAnimation';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlag } from 'types/FeatureFlags';

export interface MarketInsightVersionNotificationProps {
  prop?: any;
}

/**
 * MarketInsightVersionNotification
 */
export default function MarketInsightVersionNotification({
  prop,
}: MarketInsightVersionNotificationProps) {
  const [marketInsightVersionStatus, setMarketInsightVersionStatus] = useAtom(
    marketInsightVersionStatusAtom,
  );
  const isMarketInsightsV2Enabled = useFeatureFlag(FeatureFlag.ENABLE_MARKET_INSIGHTS_V2);

  if (!isMarketInsightsV2Enabled) {
    return null;
  }

  const location = useLocation();
  const navigate = useNavigateKeepParams();

  if (
    location.pathname !== '/analytics/market_insights' &&
    location.pathname !== '/analytics/market_insights_v2'
  ) {
    return null;
  }

  if (location.pathname === '/analytics/market_insights_v2') {
    return (
      <Flex gap='8px' alignItems='center'>
        <Text type='helper-text-01' color='tertiary'>
          v2.0 • Current
        </Text>
        <Chip
          variant='filled'
          color='blue'
          rightIcon={<IconSwitchHorizontal color='var(--chip-blue)' size={14} />}
          handleClick={() => navigate('/analytics/market_insights')}
          className={styles.notificationChip}
        >
          Switch back to v1.0
        </Chip>
      </Flex>
    );
  }

  return (
    <div className={styles.marketinsightversionnotification}>
      <Text type='helper-text-01' color='tertiary'>
        v1.0 • Current
      </Text>
      <Popover
        isHoverTriggered
        onOpenAutoFocus={(e) => e.preventDefault()}
        showCloseButton={false}
        align='end'
        popupContent={
          <Flex column width='370px'>
            <IconBulb size={26} color='var(--chip-blue)' />
            <Flex margin='12px 0 16px'>
              <Text type='label-01'>A new version of Market Insights is available</Text>
            </Flex>
            <Text type='body-01' color='secondary'>
              What's new:
            </Text>
            <Text type='body-01'>
              <Flex alignItems='center' gap='8px' margin='8px 0 0 4px'>
                <IconCircleFilled size={6} /> Identify underexposed and delivered VINs
              </Flex>
              <Flex alignItems='center' gap='8px' margin='0 0 0 4px'>
                <IconCircleFilled size={6} /> Market gap analysis
              </Flex>
              <Flex alignItems='center' gap='8px' margin='0 0 0 4px'>
                <IconCircleFilled size={6} /> Integrated map view
              </Flex>
              <Flex alignItems='center' gap='8px' margin='20px 0 16px'>
                Don't worry, you can switch back any time
              </Flex>
            </Text>
            <Button size='sm'>Click to update</Button>
          </Flex>
        }
      >
        <div
          onMouseEnter={() =>
            marketInsightVersionStatus === 'unseen' && setMarketInsightVersionStatus('seen')
          }
        >
          <Chip
            variant='filled'
            color='blue'
            leftIcon={
              marketInsightVersionStatus === 'unseen' ? <PulsingCircleAnimation /> : undefined
            }
            rightIcon={<IconArrowUpRight color='var(--chip-blue)' size={14} />}
            handleClick={() => navigate('/analytics/market_insights_v2')}
            className={styles.notificationChip}
          >
            {marketInsightVersionStatus === 'unseen' || marketInsightVersionStatus === 'seen'
              ? 'New version available — Try v2.0'
              : 'View v2.0'}
          </Chip>
        </div>
      </Popover>
    </div>
  );
}
